/** Shared learner/Admin transcript presentation; never mutates saved source turns. */
export type TranscriptSpeaker = 'coach' | 'student'

export interface TranscriptPracticeTarget {
  /** Exact English utterance the learner should say. */
  repeatTarget: string
  /** Optional reusable structure shown with secondary emphasis. */
  pattern?: string
}

export interface TranscriptBubble {
  /** Stable provider line identity. Dev/static fixtures may omit it. */
  id?: string
  who: TranscriptSpeaker
  text: string
  /** Explicit coach-authored practice material. Never inferred from text. */
  practiceTargets?: readonly TranscriptPracticeTarget[]
}

/** The final whitespace normalization used by the learner's message renderer. */
export function normalizeVisibleTranscriptText(text: string): string {
  return text
    .trim()
    .replace(/\n[ \t]*\n(?:[ \t]*\n)+/gu, '\n\n')
}

const INLINE_FENCE = /```|\*\*|__|~~|`|\*/g
const LINE_PREFIX =
  /(^|\n)(?:#{1,6}[ \t]*|---+[ \t]*(?:\n|$)|[ \t]*[-*+][ \t]+)/gm
const KEYCAP = /([0-9])️⃣|️⃣/g
const SENTENCE_BOUNDARY = /(?:[.!?]+(?:["'”’»）)\]}】」』]*)?(?=\s|$)|[。！？]+(?:["'”’»）)\]}】」』]*)?)/gu
const SAFE_CLAUSE_BOUNDARY = /(?:[,;:—–]\s+|[、，；：]\s*|\n+)/gu
const NO_SPACE_BEFORE = /^(?:[,.;:!?…'’、。！？]|\p{Pe}|\p{Pf})/u
const NO_SPACE_AFTER = /[('‘“\[{]$/u
const CJK_BOUNDARY = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u
const TITLE_ABBREVIATION = /\b(?:mr|mrs|ms|dr|prof|sr|jr|vs)\.$/iu
const CONTEXTUAL_ABBREVIATION = /\b(?:etc|e\.g|i\.e)\.$/iu
const LOWERCASE_LETTER = /^\p{Ll}/u

const MAX_CLAUSE_CHARS = 180
const MIN_CLAUSE_CHARS = 64
const MIN_REMAINDER_CHARS = 32
const MIN_ACTIVE_FRAGMENT_CHARS = 32
const MIN_ACTIVE_FRAGMENT_WORDS = 4

type SentenceUnit = { text: string; complete: boolean }
type AccumulatedTurn = {
  id: string
  who: TranscriptSpeaker
  text: string
  practiceTargets: TranscriptPracticeTarget[]
}
export type TranscriptDisplayChunk = { id: string; text: string }
export type TranscriptDisplayTurn = {
  id: string
  who: TranscriptSpeaker
  chunks: TranscriptDisplayChunk[]
  practiceTargets: readonly TranscriptPracticeTarget[]
}

/** Mirrors the server-side fallback scrubber while leaving wording intact. */
export function stripCoachTranscriptMarkdown(text: string): string {
  return text
    .replace(LINE_PREFIX, '$1')
    .replace(INLINE_FENCE, '')
    .replace(KEYCAP, (_, digit: string | undefined) => (digit ? `${digit}.` : ''))
}

function joinCoachLines(previous: string, next: string): string {
  if (/\s$/u.test(previous) || /^\s/u.test(next)) return `${previous}${next}`
  const previousLast = previous.at(-1) ?? ''
  const nextFirst = next.at(0) ?? ''
  if (
    NO_SPACE_BEFORE.test(next) ||
    next === '"' ||
    NO_SPACE_AFTER.test(previous) ||
    CJK_BOUNDARY.test(previousLast) ||
    CJK_BOUNDARY.test(nextFirst)
  ) return `${previous}${next}`
  return `${previous} ${next}`
}

function sentenceUnits(text: string): SentenceUnit[] {
  const units: SentenceUnit[] = []
  let start = 0
  for (const match of text.matchAll(SENTENCE_BOUNDARY)) {
    const end = (match.index ?? 0) + match[0].length
    const punctuationEnd = text.slice(start, end).trimEnd()
    const nextText = text.slice(end).trimStart()
    if (
      punctuationEnd.endsWith('.') &&
      (TITLE_ABBREVIATION.test(punctuationEnd) ||
        (CONTEXTUAL_ABBREVIATION.test(punctuationEnd) &&
          LOWERCASE_LETTER.test(nextText)))
    ) {
      continue
    }
    units.push({ text: text.slice(start, end), complete: true })
    start = end
  }
  if (start < text.length) {
    units.push({ text: text.slice(start), complete: false })
  }
  return units
}

/** Split structured practice copy into learner-sized sentence steps without
 * rewriting it. The protocol normally supplies one sentence; this is a
 * presentation fallback for older or malformed multi-sentence metadata. */
export function splitPracticeTargetSentences(text: string): string[] {
  const trimmed = text.trim()
  if (trimmed.length === 0) return []
  return sentenceUnits(trimmed)
    .map((unit) => unit.text.trim())
    .filter((sentence) => sentence.length > 0)
}

function splitLongSentence(text: string): string[] {
  if (text.length <= MAX_CLAUSE_CHARS) return [text]

  const candidates = [...text.matchAll(SAFE_CLAUSE_BOUNDARY)]
    .map((match) => (match.index ?? 0) + match[0].length)
  const chunks: string[] = []
  let start = 0

  while (text.length - start > MAX_CLAUSE_CHARS) {
    const boundary = candidates.filter((candidate) =>
      candidate - start >= MIN_CLAUSE_CHARS &&
      candidate - start <= MAX_CLAUSE_CHARS &&
      text.length - candidate >= MIN_REMAINDER_CHARS,
    ).at(-1)
    if (boundary === undefined) break
    chunks.push(text.slice(start, boundary))
    start = boundary
  }

  if (start === 0) return [text]
  const remainder = text.slice(start)
  if (remainder.trim().length < MIN_REMAINDER_CHARS && chunks.length > 0) {
    chunks[chunks.length - 1] += remainder
  } else {
    chunks.push(remainder)
  }
  return chunks
}

/** Pair from the start so committed prefix chunks never rebalance when a new
 * sentence arrives at the end of a streaming turn. */
function groupCompleteSentences(units: readonly SentenceUnit[]): string[] {
  const chunks: string[] = []
  for (let index = 0; index < units.length; index += 2) {
    chunks.push(`${units[index]?.text ?? ''}${units[index + 1]?.text ?? ''}`)
  }
  return chunks
}

function isTinyActiveFragment(text: string): boolean {
  const trimmed = text.trim()
  return trimmed.length < MIN_ACTIVE_FRAGMENT_CHARS &&
    trimmed.split(/\s+/u).filter(Boolean).length < MIN_ACTIVE_FRAGMENT_WORDS
}

function chunkCoachTurn(text: string): string[] {
  const units = sentenceUnits(text)
  const trailing = units.at(-1)?.complete === false ? units.pop() : undefined
  const chunks: string[] = []
  let ordinaryRun: SentenceUnit[] = []
  // An odd final sentence is the head of the next two-sentence chunk. Keep
  // its streaming successor in that same chunk so completion never removes a
  // visible bubble or moves text into an earlier chunk.
  let lastChunkAwaitsPair = false

  const flushOrdinaryRun = (): void => {
    if (ordinaryRun.length === 0) return
    chunks.push(...groupCompleteSentences(ordinaryRun))
    lastChunkAwaitsPair = ordinaryRun.length % 2 === 1
    ordinaryRun = []
  }

  for (const unit of units) {
    const clauseChunks = splitLongSentence(unit.text)
    if (clauseChunks.length === 1) ordinaryRun.push(unit)
    else {
      flushOrdinaryRun()
      if (lastChunkAwaitsPair && chunks.length > 0)
        chunks[chunks.length - 1] += clauseChunks.shift() ?? ''
      chunks.push(...clauseChunks)
      lastChunkAwaitsPair = false
    }
  }
  flushOrdinaryRun()

  if (trailing) {
    const clauseChunks = splitLongSentence(trailing.text)
    if (clauseChunks.length > 1) {
      if (
        chunks.length > 0 &&
        (lastChunkAwaitsPair || isTinyActiveFragment(chunks.at(-1) ?? ''))
      )
        chunks[chunks.length - 1] += clauseChunks.shift() ?? ''
      chunks.push(...clauseChunks)
    } else if (
      chunks.length > 0 &&
      (lastChunkAwaitsPair ||
        isTinyActiveFragment(trailing.text) ||
        isTinyActiveFragment(chunks.at(-1) ?? ''))
    ) {
      chunks[chunks.length - 1] += trailing.text
    } else {
      chunks.push(trailing.text)
    }
  }

  return chunks.length > 0 ? chunks : [text]
}

/** Derive presentation-only coach chunks. Source bubbles remain untouched, so
 * persistence, analytics, and conversation ordering still see one transcript
 * turn. Consecutive provider coach lines are one visible turn until the next
 * learner line, matching the provider lifecycle used by both realtime paths. */
export function buildTranscriptDisplayTurns(
  bubbles: readonly TranscriptBubble[],
): TranscriptDisplayTurn[] {
  const accumulated: AccumulatedTurn[] = []

  bubbles.forEach((bubble, index) => {
    const id = bubble.id ?? `transcript-${bubble.who}-${index}`
    if (bubble.who === 'student') {
      accumulated.push({
        id,
        who: bubble.who,
        text: bubble.text,
        practiceTargets: [],
      })
      return
    }

    const text = stripCoachTranscriptMarkdown(bubble.text).trim()
    const practiceTargets = [...(bubble.practiceTargets ?? [])]
    const previous = accumulated.at(-1)
    // A structured target belongs immediately before the coach segment that
    // models it. Consecutive provider persona messages are otherwise folded
    // into one visual turn, which used to move the card below every later
    // explanatory message in that turn. Start a new display segment when a
    // target arrives after visible coach text; following target-free coach
    // messages may still join this segment beneath the model.
    if (
      practiceTargets.length > 0 &&
      previous?.who === 'coach' &&
      previous.text.length > 0
    ) {
      accumulated.push({
        id,
        who: bubble.who,
        text,
        practiceTargets,
      })
      return
    }
    if (previous?.who === 'coach') {
      if (text.length > 0) {
        if (previous.text.length === 0) {
          // A turn seeded by a metadata-only practice row adopts the first
          // text-bearing line's id. The practice row is transient — deleted
          // on cancel/disconnect, reordered when a reserved interim segment
          // finalizes — so turn/chunk identity must come from the durable
          // transcription segment or the whole turn remounts mid-turn.
          previous.id = id
          previous.text = text
        } else {
          previous.text = joinCoachLines(previous.text, text)
        }
      }
      previous.practiceTargets.push(...practiceTargets)
      return
    }
    if (text.length === 0 && practiceTargets.length === 0) return
    accumulated.push({
      id,
      who: bubble.who,
      text,
      practiceTargets,
    })
  })

  return accumulated.map((turn) => {
    if (turn.who === 'student') return {
      id: turn.id,
      who: turn.who,
      chunks: [{ id: turn.id, text: turn.text }],
      practiceTargets: turn.practiceTargets,
    }
    return {
      id: turn.id,
      who: turn.who,
      chunks: turn.text.length > 0
        ? chunkCoachTurn(turn.text).map((text, index) => ({
            id: `${turn.id}:chunk:${index}`,
            text,
          }))
        : [{ id: `${turn.id}:chunk:0`, text: '' }],
      practiceTargets: turn.practiceTargets,
    }
  })
}
